/**
 * Outil « Ondes et son » (Boîte à outils, physique-chimie 4e → Tle).
 *
 * Explorer, en quatre parties selon le niveau :
 * - Propagation : une impulsion (un clap) ou une onde sinusoïdale part de la source S ; la
 *   « photo » du milieu à l'instant t et l'enregistrement au point M. Air, eau, acier ou vide ;
 *   obstacle pour l'écho ; retard τ = d / v ; en 1re, λ = v / f et la double périodicité.
 * - Son : l'enregistrement à l'oscilloscope (balayage en ms par division) ; hauteur
 *   (fréquence), timbre (diapason, flûte, violon), niveau sonore et risque ; domaine audible ;
 *   en Tle, plusieurs sources et la distance (L = 10 log(I / I₀)).
 * - Diffraction (Tle) : figure sur l'écran d'une fente ou des fentes d'Young.
 * - Doppler (Tle) : fronts d'onde d'une source qui avance, fréquences reçues devant et derrière.
 * Défi : cinq questions selon le niveau. Logique : `waves.ts`.
 */

import { useMemo, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { PlayBar, usePlayer } from '../../ui/player';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  LASERS,
  MEDIA,
  TIMBRES,
  axisLength,
  dB,
  delayAt,
  diffLines,
  diffMeasures,
  domainOf,
  doppler,
  dopplerLines,
  fr,
  hz,
  laserById,
  makeQuiz,
  meters,
  partsFor,
  propDuration,
  propLines,
  quizKinds,
  riskOf,
  screenIntensity,
  secs,
  soundAt,
  soundLevel,
  soundLines,
  speedOf,
  sweepFor,
  waveAt,
  wavefronts,
  wavelength,
  type LaserId,
  type Medium,
  type Part,
  type PropInput,
  type Signal,
  type SoundInput,
  type Timbre,
} from './waves';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-400)';
const WAVE = 'var(--color-primary-500)';
const REC = 'var(--color-accent-600)';

function Lines({ lines, testId }: { lines: string[]; testId: string }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid={testId}>
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="wv-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** lecture d'un film : t de 0 à T en `screen` secondes à l'écran */
/* ---------- propagation ---------- */

function PropagationView({ rank }: { rank: number | null }) {
  const sineAllowed = rank === null || rank >= 4;
  const [medium, setMedium] = useState<Medium>('air');
  const [signal, setSignal] = useState<Signal>(rank !== null && rank >= 6 ? 'sine' : 'pulse');
  const [f, setF] = useState(340);
  const [dPulse, setDPulse] = useState(170);
  const [dSine, setDSine] = useState(2);
  const [wall, setWall] = useState(false);
  const [D, setD] = useState(170);
  const sig: Signal = sineAllowed ? signal : 'pulse';
  const input: PropInput = {
    medium,
    signal: sig,
    f,
    d: sig === 'pulse' ? dPulse : dSine,
    wall: sig === 'pulse' && wall,
    D,
  };
  const key = JSON.stringify(input);
  const T = propDuration(input);
  const player = usePlayer(T, sig === 'pulse' ? 5 : 8, key);
  const t = player.t;
  const X = axisLength(sig);
  const v = speedOf(medium);
  const xM = input.wall ? 0 : input.d;

  // figure : photo du milieu (haut) et enregistrement (bas)
  const H = 210;
  const sx = (x: number) => 24 + (x / X) * (W - 48);
  const yPhoto = 62;
  const yRec = 160;
  const amp = 26;
  const photo = useMemo(
    () =>
      Array.from({ length: 321 }, (_, k) => {
        const x = (k / 320) * X;
        return `${k === 0 ? 'M' : 'L'} ${sx(x).toFixed(1)} ${(yPhoto - amp * waveAt(input, x, t)).toFixed(1)}`;
      }).join(' '),
    [key, t], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const tx = (s: number) => 24 + (s / T) * (W - 48);
  const rec = useMemo(() => {
    const n = 300;
    const pts: string[] = [];
    for (let k = 0; k <= n; k++) {
      const s = (k / n) * T;
      if (s > t) break;
      pts.push(
        `${k === 0 ? 'M' : 'L'} ${tx(s).toFixed(1)} ${(yRec - amp * waveAt(input, xM, s)).toFixed(1)}`,
      );
    }
    return pts.join(' ');
  }, [key, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const tau = delayAt(input, input.d);
  const rows: Array<[string, string]> = [
    ['Célérité v', v === 0 ? 'pas de propagation' : `${fr(v, 0)} m/s`],
  ];
  if (sig === 'sine') {
    rows.push(['Fréquence f', hz(f)], ['Période T', secs(1 / f)]);
    if (rank === null || rank >= 6)
      rows.push(['Longueur d’onde λ', v === 0 ? '—' : meters(wavelength(input))]);
  }
  if (input.wall) rows.push(['Écho : aller-retour', v === 0 ? '—' : secs((2 * D) / v)]);
  else rows.push([`Retard en M (d = ${meters(input.d)})`, secs(tau)]);

  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Propagation de l’onde"
        data-testid="wv-prop"
      >
        <text x={24} y={16} fontSize={11} fill={MUTED}>
          {frenchSpaces(`Le milieu à t = ${secs(t)}`)}
        </text>
        <line x1={24} y1={yPhoto} x2={W - 24} y2={yPhoto} stroke="var(--color-ink-200)" />
        {input.wall && (
          <g>
            <rect x={sx(D)} y={yPhoto - 34} width={8} height={68} fill="var(--color-ink-400)" />
            <text x={sx(D) + 12} y={yPhoto - 24} fontSize={10} fill={MUTED}>
              obstacle
            </text>
          </g>
        )}
        <path d={photo} fill="none" stroke={WAVE} strokeWidth={2} />
        <circle cx={sx(0)} cy={yPhoto} r={5} fill={INK} />
        <HaloText x={sx(0)} y={yPhoto + 20} textAnchor="middle" fontSize={11} fill={INK}>
          S
        </HaloText>
        {!input.wall && (
          <g>
            <line
              x1={sx(input.d)}
              y1={yPhoto - 32}
              x2={sx(input.d)}
              y2={yPhoto + 32}
              stroke={REC}
              strokeDasharray="3 3"
            />
            <circle
              cx={sx(input.d)}
              cy={yPhoto - amp * waveAt(input, input.d, t)}
              r={4}
              fill={REC}
            />
            <HaloText x={sx(input.d) + 5} y={yPhoto + 30} fontSize={11} fill={REC}>
              M
            </HaloText>
          </g>
        )}
        <text x={W - 24} y={yPhoto + 46} textAnchor="end" fontSize={9} fill={MUTED}>
          {frenchSpaces(`${fr(X, 0)} m`)}
        </text>
        <text x={24} y={yRec - 38} fontSize={11} fill={MUTED}>
          {input.wall ? 'Enregistrement près de la source' : 'Enregistrement en M'}
        </text>
        <line x1={24} y1={yRec} x2={W - 24} y2={yRec} stroke="var(--color-ink-200)" />
        <path d={rec} fill="none" stroke={REC} strokeWidth={2} />
        <text x={W - 24} y={yRec + 40} textAnchor="end" fontSize={9} fill={MUTED}>
          {frenchSpaces(`t (jusqu’à ${secs(T)})`)}
        </text>
      </svg>
      <PlayBar player={player} T={T} format={secs} />
      <Segmented
        ariaLabel="Milieu"
        value={medium}
        options={(Object.keys(MEDIA) as Medium[]).map((m) => ({ value: m, label: MEDIA[m].label }))}
        onChange={setMedium}
      />
      {sineAllowed && (
        <Segmented
          ariaLabel="Signal"
          value={sig}
          options={[
            { value: 'pulse', label: 'Impulsion (clap)' },
            { value: 'sine', label: 'Son continu' },
          ]}
          onChange={setSignal}
        />
      )}
      {sig === 'pulse' && (
        <ToggleChip on={wall} onClick={() => setWall(!wall)}>
          Obstacle (écho)
        </ToggleChip>
      )}
      {sig === 'pulse' && !wall && (
        <ParamSlider
          name="Distance de M"
          ariaLabel="Distance de M"
          value={dPulse}
          min={20}
          max={400}
          step={10}
          onChange={setDPulse}
          format={(x) => frenchSpaces(meters(x))}
        />
      )}
      {sig === 'pulse' && wall && (
        <ParamSlider
          name="Distance de l’obstacle"
          ariaLabel="Distance de l’obstacle"
          value={D}
          min={50}
          max={200}
          step={10}
          onChange={setD}
          format={(x) => frenchSpaces(meters(x))}
        />
      )}
      {sig === 'sine' && (
        <>
          <ParamSlider
            name="Fréquence"
            ariaLabel="Fréquence"
            value={f}
            min={100}
            max={1000}
            step={10}
            onChange={setF}
            format={(x) => frenchSpaces(hz(x))}
          />
          <ParamSlider
            name="Distance de M"
            ariaLabel="Distance de M"
            value={dSine}
            min={0.2}
            max={4}
            step={0.1}
            onChange={setDSine}
            format={(x) => frenchSpaces(meters(x))}
          />
        </>
      )}
      <Readout rows={rows} />
      <Lines lines={propLines(input, t, rank)} testId="wv-law" />
    </div>
  );
}

/* ---------- son ---------- */

function Oscillo({ input, L }: { input: SoundInput; L: number }) {
  const H = 170;
  const gx = 20;
  const gy = 10;
  const gw = W - 40;
  const gh = 150;
  const div = gw / 10;
  const sweep = sweepFor(input.f); // ms / div
  const span = (10 * sweep) / 1000; // s
  const a = (0.15 + (0.85 * Math.min(Math.max(L, 0), 120)) / 120) * (gh / 2 - 6);
  const d = Array.from({ length: 401 }, (_, k) => {
    const t = (k / 400) * span;
    return `${k === 0 ? 'M' : 'L'} ${(gx + (k / 400) * gw).toFixed(1)} ${(gy + gh / 2 - a * soundAt(input.timbre, input.f, t)).toFixed(1)}`;
  }).join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H + 16}`}
      className="w-full rounded-2xl bg-[#0b1f17]"
      role="img"
      aria-label={`Oscillogramme, balayage ${fr(sweep, 2)} ms par division`}
      data-testid="wv-oscillo"
    >
      {Array.from({ length: 11 }, (_, i) => (
        <line
          key={`v${i}`}
          x1={gx + i * div}
          y1={gy}
          x2={gx + i * div}
          y2={gy + gh}
          stroke="#1f4d3a"
          strokeWidth={i === 5 ? 1.2 : 0.8}
        />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <line
          key={`h${i}`}
          x1={gx}
          y1={gy + (i * gh) / 8}
          x2={gx + gw}
          y2={gy + (i * gh) / 8}
          stroke="#1f4d3a"
          strokeWidth={i === 4 ? 1.2 : 0.8}
        />
      ))}
      <path d={d} fill="none" stroke="#4ade80" strokeWidth={2} />
      <text x={W - 20} y={H + 10} textAnchor="end" fontSize={11} fill="#86efac">
        {frenchSpaces(`Balayage : ${fr(sweep, 2)} ms/div`)}
      </text>
    </svg>
  );
}

function DomainBar({ f }: { f: number }) {
  const lo = 1;
  const hi = Math.log10(40000);
  const x = (v: number) => 4 + ((Math.log10(v) - lo) / (hi - lo)) * 92;
  return (
    <div className="space-y-1" aria-hidden>
      <div className="relative h-6 overflow-hidden rounded-full text-[10px] font-semibold">
        <div className="bg-ink-200 absolute inset-y-0" style={{ left: 0, width: `${x(20)}%` }} />
        <div
          className="bg-success-500/30 absolute inset-y-0"
          style={{ left: `${x(20)}%`, width: `${x(20000) - x(20)}%` }}
        />
        <div className="bg-ink-200 absolute inset-y-0" style={{ left: `${x(20000)}%`, right: 0 }} />
        <span className="text-ink-600 absolute left-1 top-1">infra</span>
        <span
          className="text-ink-700 absolute top-1"
          style={{ left: `${(x(20) + x(20000)) / 2 - 6}%` }}
        >
          audible
        </span>
        <span className="text-ink-600 absolute right-1 top-1">ultra</span>
        <div
          className="bg-primary-500 absolute inset-y-0 w-1 rounded"
          style={{ left: `calc(${x(f)}% - 2px)` }}
        />
      </div>
      <div className="text-ink-500 relative h-3 whitespace-nowrap text-[10px]">
        <span className="absolute" style={{ left: `${x(20)}%`, transform: 'translateX(-50%)' }}>
          20 Hz
        </span>
        <span className="absolute" style={{ left: `${x(20000)}%`, transform: 'translateX(-50%)' }}>
          20 kHz
        </span>
      </div>
    </div>
  );
}

function LevelBar({ L }: { L: number }) {
  const x = (v: number) => Math.min(100, Math.max(0, (v / 130) * 100));
  return (
    <div className="space-y-1" aria-hidden data-testid="wv-level">
      <div className="relative h-4 overflow-hidden rounded-full">
        <div
          className="bg-success-500/40 absolute inset-y-0"
          style={{ left: 0, width: `${x(85)}%` }}
        />
        <div
          className="absolute inset-y-0 bg-amber-400/50"
          style={{ left: `${x(85)}%`, width: `${x(100) - x(85)}%` }}
        />
        <div
          className="bg-error-500/40 absolute inset-y-0"
          style={{ left: `${x(100)}%`, right: 0 }}
        />
        <div
          className="bg-ink-900 absolute inset-y-0 w-1 rounded"
          style={{ left: `calc(${x(L)}% - 2px)` }}
        />
      </div>
      <div className="text-ink-500 relative h-3 whitespace-nowrap text-[10px]">
        <span className="absolute left-0">0 dB</span>
        <span className="absolute" style={{ left: `${x(85)}%`, transform: 'translateX(-50%)' }}>
          85
        </span>
        <span className="absolute" style={{ left: `${x(100)}%`, transform: 'translateX(-50%)' }}>
          100
        </span>
        <span className="absolute" style={{ left: `${x(120)}%`, transform: 'translateX(-50%)' }}>
          120 dB
        </span>
      </div>
    </div>
  );
}

function SoundView({ rank }: { rank: number | null }) {
  const tle = rank === 7;
  const [logf, setLogf] = useState(Math.log10(440));
  const [timbre, setTimbre] = useState<Timbre>('pure');
  const [L, setL] = useState(60);
  const [n, setN] = useState(1);
  const [dist, setDist] = useState(1);
  const f =
    Math.round(10 ** logf / (10 ** logf >= 1000 ? 100 : 10)) * (10 ** logf >= 1000 ? 100 : 10);
  const input: SoundInput = { f, timbre, L, n, dist };
  const level = soundLevel(input, rank);
  return (
    <div className="space-y-4">
      <Oscillo input={input} L={level} />
      <ParamSlider
        name="Fréquence (hauteur)"
        ariaLabel="Fréquence"
        value={logf}
        min={1}
        max={4.6}
        step={0.01}
        onChange={setLogf}
        format={() => frenchSpaces(hz(f))}
      />
      <DomainBar f={f} />
      <Segmented
        ariaLabel="Timbre"
        value={timbre}
        options={(Object.keys(TIMBRES) as Timbre[]).map((k) => ({
          value: k,
          label: TIMBRES[k].label,
        }))}
        onChange={setTimbre}
      />
      {tle ? (
        <>
          <Stepper label="Sources identiques" value={n} min={1} max={10} onChange={setN} />
          <ParamSlider
            name="Distance aux sources"
            ariaLabel="Distance aux sources"
            value={dist}
            min={1}
            max={16}
            step={1}
            onChange={setDist}
            format={(x) => frenchSpaces(meters(x))}
          />
        </>
      ) : (
        <ParamSlider
          name="Niveau sonore"
          ariaLabel="Niveau sonore"
          value={L}
          min={0}
          max={120}
          step={5}
          onChange={setL}
          format={(x) => frenchSpaces(dB(x))}
        />
      )}
      <LevelBar L={level} />
      <Readout
        rows={[
          ['Fréquence f', hz(f)],
          ['Période T', secs(1 / f)],
          [
            'Domaine',
            domainOf(f) === 'audible'
              ? 'audible'
              : domainOf(f) === 'infrason'
                ? 'infrason'
                : 'ultrason',
          ],
          ['Niveau L', dB(level)],
          ['Risque', riskOf(level)],
        ]}
      />
      <Lines lines={soundLines(input, rank)} testId="wv-law" />
    </div>
  );
}

/* ---------- diffraction ---------- */

function DiffractionView() {
  const [laser, setLaser] = useState<LaserId>('red');
  const [a, setA] = useState(100);
  const [D, setD] = useState(2);
  const [young, setYoung] = useState(false);
  const [b, setB] = useState(300);
  const input = { laser, a, D, young, b };
  const m = diffMeasures(input);
  const color = laserById(laser).color;
  const Xmax = 1.25 * m.L; // m, de part et d'autre du centre
  const H = 150;
  const n = 260;
  const sx = (X: number) => W / 2 + (X / Xmax) * (W / 2 - 16);
  const curve = Array.from({ length: n + 1 }, (_, k) => {
    const X = -Xmax + (2 * Xmax * k) / n;
    return `${k === 0 ? 'M' : 'L'} ${sx(X).toFixed(1)} ${(138 - 56 * screenIntensity(input, X)).toFixed(1)}`;
  }).join(' ');
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full rounded-2xl bg-[#0b0b10]"
        role="img"
        aria-label="Figure sur l’écran"
        data-testid="wv-screen"
      >
        {Array.from({ length: n }, (_, k) => {
          const X = -Xmax + (2 * Xmax * (k + 0.5)) / n;
          const I = screenIntensity(input, X);
          return (
            <rect
              key={k}
              x={sx(X) - (W - 32) / n / 2}
              y={14}
              width={(W - 32) / n + 0.6}
              height={46}
              fill={color}
              opacity={Math.min(1, I ** 0.6)}
            />
          );
        })}
        <line
          x1={sx(-m.L / 2)}
          y1={66}
          x2={sx(m.L / 2)}
          y2={66}
          stroke="#e5e7eb"
          strokeWidth={1.5}
        />
        <text x={W / 2} y={78} textAnchor="middle" fontSize={10} fill="#e5e7eb">
          {frenchSpaces(`L = ${meters(m.L)}`)}
        </text>
        <path d={curve} fill="none" stroke={color} strokeWidth={1.8} />
        <line x1={16} y1={138} x2={W - 16} y2={138} stroke="#4b5563" />
      </svg>
      <Segmented
        ariaLabel="Laser"
        value={laser}
        options={LASERS.map((l) => ({ value: l.id, label: `${l.label} ${l.nm} nm` }))}
        onChange={setLaser}
      />
      <ParamSlider
        name="Largeur de la fente a"
        ariaLabel="Largeur de la fente"
        value={a}
        min={20}
        max={200}
        step={10}
        onChange={setA}
        format={(x) => frenchSpaces(`${fr(x, 0)} µm`)}
      />
      <ParamSlider
        name="Distance à l’écran D"
        ariaLabel="Distance à l’écran"
        value={D}
        min={1}
        max={3}
        step={0.5}
        onChange={setD}
        format={(x) => frenchSpaces(meters(x))}
      />
      <ToggleChip on={young} onClick={() => setYoung(!young)}>
        Deux fentes (Young)
      </ToggleChip>
      {young && (
        <ParamSlider
          name="Distance entre les fentes b"
          ariaLabel="Distance entre les fentes"
          value={b}
          min={100}
          max={500}
          step={50}
          onChange={setB}
          format={(x) => frenchSpaces(`${fr(x, 0)} µm`)}
        />
      )}
      <Readout
        rows={[
          ['Demi-angle θ = λ ÷ a', `${fr(m.theta * 1000, 1)} mrad`],
          ['Tache centrale L', meters(m.L)],
          ...(young ? ([['Interfrange i', meters(m.i)]] as Array<[string, string]>) : []),
        ]}
      />
      <Lines lines={diffLines(input)} testId="wv-law" />
    </div>
  );
}

/* ---------- Doppler ---------- */

function DopplerView() {
  const [vE, setVE] = useState(80);
  const [fE, setFE] = useState(440);
  const H = 190;
  const s = (W - 20) / 360; // px par m
  const cx0 = W / 2 - 40;
  const fronts = wavefronts(vE, 0.05, 0.5);
  const src = vE * 0.5;
  const d = doppler(fE, vE);
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Fronts d’onde d’une source en mouvement"
        data-testid="wv-doppler"
      >
        <defs>
          <clipPath id="wv-clip">
            <rect x={0} y={0} width={W} height={H} />
          </clipPath>
        </defs>
        <g clipPath="url(#wv-clip)">
          {fronts.map((c, i) => (
            <circle
              key={i}
              cx={cx0 + c.cx * s}
              cy={H / 2}
              r={c.r * s}
              fill="none"
              stroke={WAVE}
              strokeWidth={1.2}
              opacity={0.75}
            />
          ))}
        </g>
        <circle cx={cx0 + src * s} cy={H / 2} r={6} fill="var(--color-error-500)" />
        <text x={cx0 + src * s} y={H / 2 - 10} textAnchor="middle" fontSize={10} fill={INK}>
          source →
        </text>
        <circle cx={W - 14} cy={H / 2} r={5} fill={INK} />
        <HaloText x={W - 14} y={H / 2 + 20} textAnchor="end" fontSize={10} fill={INK}>
          {frenchSpaces(`devant : ${hz(d.ahead)}`)}
        </HaloText>
        <circle cx={14} cy={H / 2} r={5} fill={INK} />
        <HaloText x={14} y={H / 2 + 20} fontSize={10} fill={INK}>
          {frenchSpaces(`derrière : ${hz(d.behind)}`)}
        </HaloText>
      </svg>
      <ParamSlider
        name="Vitesse de la source"
        ariaLabel="Vitesse de la source"
        value={vE}
        min={0}
        max={200}
        step={5}
        onChange={setVE}
        format={(x) => frenchSpaces(`${fr(x, 0)} m·s⁻¹ (${fr(x * 3.6, 0)} km/h)`)}
      />
      <ParamSlider
        name="Fréquence émise"
        ariaLabel="Fréquence émise"
        value={fE}
        min={200}
        max={1000}
        step={20}
        onChange={setFE}
        format={(x) => frenchSpaces(hz(x))}
      />
      <Readout
        rows={[
          ['f_E (émise)', hz(fE)],
          ['f_R devant', hz(d.ahead)],
          ['f_R derrière', hz(d.behind)],
        ]}
      />
      <Lines lines={dopplerLines(fE, vE)} testId="wv-law" />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      {parts.length > 1 && (
        <Segmented
          ariaLabel="Partie"
          value={part}
          options={parts.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setPart}
        />
      )}
      {part === 'propagation' && <PropagationView rank={rank} />}
      {part === 'sound' && <SoundView rank={rank} />}
      {part === 'diffraction' && <DiffractionView />}
      {part === 'doppler' && <DopplerView />}
    </div>
  );
}

export function WavesTool({
  level = null,
  seed,
}: {
  /** niveau : parties, grandeurs et lois selon la classe (4e → Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="wv" rich />
      </div>
    </div>
  );
}
